import { type SiteSettings, SiteSettingsSchema } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { Save } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { QueryState } from "@/components/query-state";
import { usePutSiteSettings, useSiteSettings } from "@/features/cms/queries";
import { describe, type FieldSpec } from "@/features/cms/schema-form/introspect";
import { SchemaForm } from "@/features/cms/schema-form/schema-form";

const SITE_FIELDS: Record<string, FieldSpec> = (() => {
  const s = describe(SiteSettingsSchema);
  return s.kind === "object" ? s.fields : {};
})();

function SiteForm({ initial }: { initial: Partial<SiteSettings> }) {
  const canWrite = usePermission("cms:write");
  const put = usePutSiteSettings();
  const [value, setValue] = useState<Record<string, unknown>>(() => {
    // Fill defaults so nested sections render populated.
    const parsed = SiteSettingsSchema.safeParse(initial);
    return parsed.success ? parsed.data : { ...initial };
  });
  const [dirty, setDirty] = useState(false);
  const parsed = useMemo(() => SiteSettingsSchema.safeParse(value), [value]);
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!parsed.success) return;
        await put.mutateAsync(parsed.data);
        setDirty(false);
      }}
    >
      <div className="flex items-center gap-2">
        {dirty ? <StatusTag variant="warn">UNSAVED</StatusTag> : null}
        {!parsed.success ? (
          <StatusTag variant="err">{parsed.error.issues.length} INVALID</StatusTag>
        ) : null}
        <div className="flex-1" />
        {canWrite ? (
          <Button type="submit" disabled={!dirty || !parsed.success || put.isPending}>
            <Save /> {put.isPending ? "Saving…" : "Save settings"}
          </Button>
        ) : null}
      </div>
      <div className="rounded-xl border bg-card p-4">
        <SchemaForm
          fields={SITE_FIELDS}
          value={value}
          onChange={(v) => {
            setValue(v);
            setDirty(true);
          }}
          issues={parsed.success ? undefined : parsed.error.issues}
          disabled={!canWrite}
        />
      </div>
    </form>
  );
}

export function CmsSitePage() {
  const q = useSiteSettings();
  return (
    <>
      <PageHeader
        title="Site settings"
        description="Navigation, footer, social links and SEO defaults for the landing site."
      />
      <QueryState query={q} rows={8}>
        {(s) => <SiteForm initial={s} />}
      </QueryState>
    </>
  );
}
